import {
  NavigationGuardNext,
  RouteLocationNormalized,
  Router,
  RouterLink,
} from "vue-router";
import { msalInstance, loginRequest } from "../authConfig";
import {
  InteractionType,
  PopupRequest,
  PublicClientApplication,
  RedirectRequest,
} from "@azure/msal-browser";
import store from "@/store/index";
import { checkTokenFromFCH } from "@/requests/checkTokenFromFCH";
import useSSOFunction from "@/composables/composableGestioneSSO";
const { statusFunction, puoNavigare, logoutFunction } = useSSOFunction();

export function registerGuard(router: Router) {
  const accounts = msalInstance.getAllAccounts();

  router.beforeEach(
    async (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
      // Prima cosa: persisti il percorso richiesto per supportare apertura in blank
      try {
        const key = "intendedRouteFirst";
        if (
          !sessionStorage.getItem(key) &&
          to.fullPath &&
          to.fullPath !== "/"
        ) {
          sessionStorage.setItem(key, to.fullPath);
        }
      } catch {
        // console.log("Impossibile memorizzare la rotta richiesta");
      }

      // Pagine sempre accessibili senza attendere menu/pagine autorizzate
      const isWhitelisted = (r: RouteLocationNormalized) => {
        const p = r.path;
        return (
          p === "/logout" ||
          p === "/logout-post" ||
          p === "/404" ||
          p === "/manutenzione" ||
          p === "/non-autorizzato" ||
          p === "/vt" ||
          p === "/verifica-tessera"
        );
      };
      if (isWhitelisted(to)) return true;

      if (to.query.FCHT) {
        const puoEntrareUtenteFCHStatus = await checkTokenFromFCH(
          to.query.FCHT
        );
        localStorage.setItem("utenteFCH", "1");
        localStorage.setItem("FCHT", to.query.FCHT as string);
        localStorage.setItem(
          "puoEntrareUtenteFCHStatus",
          puoEntrareUtenteFCHStatus.toString()
        );
      }
      if (to.hash.includes("AADB2C90118") || to.hash.includes("AADB2C90091")) {
        store.commit("setIsLoggingAdb2c");
      }
      if (!accounts.length && to.meta.noAuth === true) {
        return true;
      } else {
        await statusFunction();

        if (to.fullPath === "/sign-in") return "/";

        const request = {
          ...loginRequest,
          redirectStartPage: to.fullPath,
        };
        const shouldProceed = await isAuthenticated(
          msalInstance,
          InteractionType.Redirect,
          request
        );

        if (!(shouldProceed && puoNavigare.value)) return false;

        // Attendi che l'elenco pagine autorizzate sia disponibile prima di procedere
        const waitForAuthorizedPages = async (
          timeoutMs = 0
        ): Promise<boolean> => {
          const triggerFetchIfToken = async () => {
            try {
              const isMenuLoaded = (store.getters as any).isMenuLoaded;
              const token = localStorage.getItem("tokenAdb2c");
              if (!isMenuLoaded && token) {
                await (store.dispatch as any)("getMenuFromApi", 0);
                await (store.dispatch as any)("lookupsStagioniFromApi");
              }
            } catch (e) {
              // la store impedisce doppie chiamate
            }
          };
          const pagesNow: string[] =
            ((store.getters as any).pagineAutorizzate as string[]) || [];
          if (pagesNow && pagesNow.length > 0) return true;
          await triggerFetchIfToken();
          return await new Promise<boolean>((resolve) => {
            let timeoutId: number | null = null;
            const cleanup = () => {
              if (unwatchPages) unwatchPages();
              if (unwatchToken) unwatchToken();
              window.removeEventListener("storage", onStorage);
              if (timeoutId) window.clearTimeout(timeoutId);
            };
            const unwatchPages = (store as any).watch(
              (_: any, getters: any) => getters.pagineAutorizzate,
              (pages: string[]) => {
                if (Array.isArray(pages) && pages.length > 0) {
                  cleanup();
                  resolve(true);
                }
              }
            );
            const unwatchToken = (store as any).watch(
              (_: any, getters: any) => getters.tokenAdb2c,
              async (val: string) => {
                if (val) await triggerFetchIfToken();
              }
            );
            const onStorage = (e: StorageEvent) => {
              if (e.key === "tokenAdb2c" && e.newValue) {
                triggerFetchIfToken();
              }
            };
            window.addEventListener("storage", onStorage);
            if (timeoutMs > 0) {
              timeoutId = window.setTimeout(() => {
                cleanup();
                resolve(false);
              }, timeoutMs);
            }
          });
        };

        const normalizeAuthorizedPages = (pages: unknown): string[] => {
          if (!Array.isArray(pages)) return [];
          return pages
            .map((pagina) => {
              if (typeof pagina === "string") return pagina;
              if (pagina && typeof pagina === "object") {
                const { route } = pagina as { route?: unknown };
                if (typeof route === "string") return route;
              }
              return null;
            })
            .filter((p): p is string => typeof p === "string" && p.length > 0);
        };

        const getAuthorizedPages = (): string[] =>
          normalizeAuthorizedPages((store.getters as any).pagineAutorizzate);

        let isMenuLoaded = (store.getters as any).isMenuLoaded;
        let pagineAutorizzate: string[] = getAuthorizedPages();
        // Memorizza la rotta richiesta per supportare apertura in nuova scheda
        try {
          sessionStorage.setItem("intendedRoute", to.fullPath);
        } catch {
          console.log("Impossibile memorizzare la rotta richiesta");
        }
        if (!isMenuLoaded) {
          await waitForAuthorizedPages();
          isMenuLoaded = (store.getters as any).isMenuLoaded;
          pagineAutorizzate = getAuthorizedPages();
        }

        const isAuthorizedPath = (targetPath: string): boolean => {
          // console.log("Verifica autorizzazione per", targetPath);
          if (pagineAutorizzate.length === 0) return false;
          const pathOnly = targetPath.split("?")[0].split("#")[0];
          // Confronta solo fino alla parte prima del primo segmento numerico
          const trimmedAtNumber = pathOnly.replace(/\/\d+(?:\/.*)?/, "");
          // Variante con segmenti numerici normalizzati a :id (es. /:id)
          const normalizeNumericSegments = (p: string) =>
            p.replace(/\/\d+(?=\/|$)/g, "/:id");

          // console.log("Verifica autorizzazione per", pathOnly, trimmedAtNumber);

          // 1) Confronto diretto con il path tagliato prima del numero
          if (pagineAutorizzate.includes(trimmedAtNumber)) return true;

          // 2) Confronto con i segmenti numerici normalizzati a :id
          const normalized = normalizeNumericSegments(pathOnly);
          // console.log("Verifica autorizzazione per", normalized);

          if (pagineAutorizzate.includes(normalized)) return true;

          // 3) Fallback: regex dai pattern con parametri dinamici
          const testAgainst = (p: string) =>
            pagineAutorizzate.some((pagina) => {
              // console.log("Verifica autorizzazione per", pagina, p);
              if (!pagina) return false;
              const pattern =
                "^" +
                pagina
                  .replace(/\\/g, "\\\\")
                  .replace(/\./g, "\\.")
                  .replace(/\+/g, "\\+")
                  .replace(/\*/g, ".*")
                  .replace(/\/:([^/]+)/g, "/[^/]+") +
                "(?:$|/)";
              try {
                return new RegExp(pattern).test(p);
              } catch {
                return false;
              }
            });

          // console.log(
          //   "Verifica autorizzazione per",
          //   testAgainst(trimmedAtNumber),
          //   testAgainst(pathOnly)
          // );
          return testAgainst(trimmedAtNumber) || testAgainst(pathOnly);
        };

        const hasWildcard =
          pagineAutorizzate.includes("*") || pagineAutorizzate.includes("/");
        if (isMenuLoaded && (pagineAutorizzate.length === 0 || hasWildcard)) {
          return true;
        }

        // console.log("Verifica autorizzazione per", from.path, to.path);

        const autorizzatoVisitarePagina = isAuthorizedPath(to.path);

        // Se esiste una rotta memorizzata ed è autorizzata, reindirizza lì
        try {
          const intended = sessionStorage.getItem("intendedRoute");
          // console.log(
          //   "Verifica autorizzazione per",
          //   intended,
          //   intended !== to.fullPath,
          //   intended === to.fullPath,
          //   autorizzatoVisitarePagina,
          //   isAuthorizedPath(intended!)
          // );

          if (
            intended &&
            intended !== to.fullPath &&
            isAuthorizedPath(intended)
          ) {
            // console.log("Verifica autorizzazione per", from.path, to.path);

            sessionStorage.removeItem("intendedRoute");
            return intended;
          }
          if (autorizzatoVisitarePagina && intended === to.fullPath) {
            // console.log("Verifica autorizzazione per", from.path, to.path);

            sessionStorage.removeItem("intendedRoute");
          }
          sessionStorage.removeItem("intendedRoute");
        } catch {
          // console.log("Impossibile recuperare la rotta memorizzata");
        }

        if (
          !to.fullPath.includes(process.env.VUE_APP_TEXT ?? "") &&
          pagineAutorizzate.length > 0 &&
          !autorizzatoVisitarePagina &&
          to.fullPath != "/" &&
          !isWhitelisted(to)
        ) {
          // sessionStorage.removeItem("intendedRoute");
          // Se la verifica avviene subito dopo il login, reindirizza al primo percorso disponibile
          const justLoggedIn =
            from.fullPath === "/" ||
            from.fullPath === "/sign-in" ||
            from.name === "Loader";

          // Se c'è un intendedRoute (apertura in blank) e NON è autorizzata, porta a non-autorizzato
          try {
            const intended = sessionStorage.getItem("intendedRoute");
            if (intended && intended !== "/") {
              return "/non-autorizzato";
            }
          } catch {
            // console.log("Impossibile recuperare la rotta memorizzata");
          }

          if (justLoggedIn) {
            const menuStore: any = (store.getters as any).menu;
            let fallbackRoute: string | undefined;
            if (Array.isArray(menuStore) && menuStore.length > 0) {
              const firstPages = menuStore[0]?.pages;
              if (Array.isArray(firstPages) && firstPages.length > 0) {
                fallbackRoute = firstPages[0].route;
              }
            }
            if (!fallbackRoute) {
              const candidates = pagineAutorizzate
                .map((p) => p.replace(/\/:[^/]+/g, ""))
                .filter((p) => !!p);
              fallbackRoute =
                candidates.find((p) => !p.includes(":")) ||
                candidates[0] ||
                "/";
            }
            return fallbackRoute;
          }

          // Altrimenti porta alla pagina non autorizzato
          return "/non-autorizzato";
        }

        return true;
      }
    }
  );
}

export async function isAuthenticated(
  instance: PublicClientApplication,
  interactionType: InteractionType,
  loginRequest: PopupRequest | RedirectRequest
): Promise<boolean> {
  // If your application uses redirects for interaction, handleRedirectPromise must be called and awaited on each page load before determining if a user is signed in or not

  return instance
    .handleRedirectPromise()
    .then(() => {
      const accounts = instance.getAllAccounts();
      if (accounts.length > 0) {
        return true;
      }

      // User is not signed in and attempting to access protected route. Sign them in.
      if (interactionType === InteractionType.Popup) {
        return instance
          .loginPopup(loginRequest)
          .then(() => {
            return true;
          })
          .catch(() => {
            return false;
          });
      } else if (interactionType === InteractionType.Redirect) {
        return instance
          .loginRedirect(loginRequest)
          .then(() => {
            return true;
          })
          .catch(() => {
            return false;
          });
      }

      return false;
    })
    .catch((e) => {
      logoutFunction();
      // instance.logoutRedirect();
      return false;
    });
}
